/**
 * SA-448 (spec 2026-09-22 §4): the process map as a read-only canvas.
 * Three layers in one CSS-transformed element — bands, edges and stub
 * arrows beneath; the cards as HTML; the labels last, with a halo — over the
 * pure layout (`layout.ts`). Columns come from the container's width (D3),
 * the viewport from `useMapViewport` (§4.3); below 24rem it is a list.
 * Nothing is draggable and nothing writes (wireframe decision 12).
 * SA-1077: the current card is lifted and the three line kinds are drawn and
 * named here, in the draw phase; `layout.ts` is untouched.
 */
import { skippedSteps } from '../pathSkips'
import { useCallback, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Expand, ZoomIn, ZoomOut } from 'lucide-react'
import { Button, Tip } from '@/components/ui'
import type { ProcessOverviewVM } from '@/utils/workflowOverview'
import { cn } from '@/utils'
import { legendEntries, lineDash, lineLegendEntries } from '../executorTone'
import { layoutProcessMap } from './layout'
import { MAP_GRID as G, type CardBox, type LabelBox } from './mapTypes'
import { MapCard } from './MapCard'
import { MapList } from './MapList'
import { useContainerWidth } from './useContainerWidth'
import { useMapViewport } from './useMapViewport'

export interface ProcessMapProps {
  vm: ProcessOverviewVM
  currentStep?: string | null
  /** D2: the exits the record took; absent on the workflow page. */
  path?: { step: string; exit: string | null }[]
  onSelect?: (id: string) => void
  /** D11: when set, a card is a link here instead of a button. */
  stepHref?: (id: string) => string
  /** SA-1328: the step a landing names; its card is ringed. */
  ringStep?: string | null
  /** SA-460 (spec §4.3): where the zoom buttons go. `undefined` = above the
   * viewport, as before; `null` = the host will hand over an element (render
   * none yet, so nothing flashes above the map); an element = portal them. */
  toolbarTarget?: HTMLElement | null
  /** D8: the shipped fourteen steps lay out 1026px tall at scale 1 (970 until
   * SA-1223's step 11 close stub); the default shows them whole on a wide screen and still
   * caps a larger definition. */
  maxHeight?: number
  /** The legend's last line; `null` omits it (an author surface where a click is not the drawer). */
  legendHint?: string | null
}

const LABEL_CLASS = 'stroke-card text-[10.5px] [paint-order:stroke] [stroke-linejoin:round] [stroke-width:4px]'

function Label({ box, taken, stubLabel }: { box: LabelBox; taken: boolean; stubLabel?: string }) {
  return (
    <text x={box.x} y={box.y} textAnchor={box.anchor} data-stub-label={stubLabel}
      className={`${LABEL_CLASS} ${taken ? 'fill-foreground' : 'fill-muted-foreground'}`}>
      {box.text}
    </text>
  )
}

/** The legend (spec §4.3): the five hue swatches from `legendEntries()`, the
 * three line kinds from `lineLegendEntries()` (SA-1077), then the host's
 * invitation. Part of the map, so every mount carries it. */
function Legend({ hint }: { hint: string | null }) {
  return (
    <div data-testid="process-map-legend" className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      {legendEntries().map((e) => (
        <span key={e.hue} className="inline-flex items-center gap-1.5">
          <i aria-hidden="true" className="inline-block size-2.5 shrink-0 rounded-sm" style={e.style} />
          {e.label}
        </span>
      ))}
      {lineLegendEntries().map((e) => (
        <span key={e.kind} className="inline-flex items-center gap-1.5">
          <svg aria-hidden="true" data-testid="process-map-legend-line" width="18" height="8" className={cn('shrink-0', e.warning && 'text-warning')}>
            <line x1="0" y1="4" x2="18" y2="4" stroke="currentColor" strokeWidth="1.5" strokeDasharray={e.dash ?? undefined} />
          </svg>
          <span className={e.warning ? 'text-warning-strong' : undefined}>{e.label}</span>
        </span>
      ))}
      {hint && <span>{hint}</span>}
    </div>
  )
}

export function ProcessMap({ vm, currentStep = null, path, onSelect, stepHref, ringStep = null, toolbarTarget, maxHeight = 1100, legendHint = 'Click a step for its card.' }: ProcessMapProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const width = useContainerWidth(rootRef)
  const columns = width ? Math.max(G.MIN_COLUMNS, Math.floor((width - G.X0 - G.PAD_RIGHT + G.COL_GAP) / G.COL_PITCH)) : G.DEFAULT_COLUMNS
  const listMode = width > 0 && width < G.LIST_FLOOR_PX
  const layout = useMemo(() => layoutProcessMap(vm, { columns }), [vm, columns])
  const viewport = useMapViewport({ contentWidth: layout.width, contentHeight: layout.height, viewportWidth: width || layout.width, maxHeight, viewportRef })
  const markerId = useId()

  const visited = useMemo(() => new Set((path ?? []).map((p) => p.step)), [path])
  const taken = useMemo(() => new Set((path ?? []).filter((p) => p.exit).map((p) => `${p.step}:${p.exit}`)), [path])
  const skipped = useMemo(() => new Set(skippedSteps(vm, path, currentStep).keys()), [vm, path, currentStep])
  const cardOf = useMemo(() => new Map(layout.cards.map((c) => [c.id, c])), [layout])
  const order = useMemo(() => vm.steps.map((s) => s.id), [vm])
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const roving = focusedId && order.includes(focusedId) ? focusedId : (currentStep && order.includes(currentStep) ? currentStep : (order.includes(vm.entry) ? vm.entry : order[0]))

  const focusCard = useCallback((id: string) => {
    setFocusedId(id)
    const escaped = CSS.escape(id)
    rootRef.current?.querySelector<HTMLElement>(`[data-step-id="${escaped}"] > button, [data-step-id="${escaped}"] > a`)?.focus()
  }, [])
  const onFocus = useCallback((id: string) => {
    setFocusedId(id)
    const c = cardOf.get(id)
    if (c && !listMode) viewport.reveal(c)
  }, [cardOf, listMode, viewport])
  const move = (id: string, key: string): string | null => {
    const i = order.indexOf(id)
    if (key === 'Home') return order[0]
    if (key === 'End') return order[order.length - 1]
    if (key === 'ArrowRight' || (listMode && key === 'ArrowDown')) return order[Math.min(order.length - 1, i + 1)]
    if (key === 'ArrowLeft' || (listMode && key === 'ArrowUp')) return order[Math.max(0, i - 1)]
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      const me = cardOf.get(id)
      if (!me) return null
      const candidates = layout.cards.filter((c) => c.globalRow === me.globalRow + (key === 'ArrowDown' ? 1 : -1))
      if (!candidates.length) return null
      // §4.6: the nearest card by CENTRE x. Every card the layout emits is
      // CARD_W wide, so this orders identically to the left edges — written
      // the way the spec words it, so a future card of another width is right.
      const mid = (c: CardBox) => c.x + c.w / 2
      return candidates.reduce((best, c) => (Math.abs(mid(c) - mid(me)) < Math.abs(mid(best) - mid(me)) ? c : best)).id
    }
    return null
  }
  const onKeyDown = (e: KeyboardEvent<HTMLElement>, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.currentTarget.click()
      return
    }
    const next = move(id, e.key)
    if (next && next !== id) {
      e.preventDefault()
      focusCard(next)
    }
  }

  const current = currentStep && order.includes(currentStep) ? vm.steps.find((s) => s.id === currentStep)! : null
  const name = `${vm.name}: ${vm.steps.length} steps in ${layout.lanes.length} lanes${current ? `, current step ${current.number} ${current.title}` : ''}`

  if (listMode) {
    return (
      <div ref={rootRef} data-testid="process-map" data-mode="list" data-columns={columns} role="group" aria-label={name}>
        <MapList vm={vm} currentStep={currentStep} visited={visited} skipped={skipped} ringStep={ringStep} focusedId={roving} href={stepHref} onSelect={onSelect}
          onKeyDown={onKeyDown} onFocus={onFocus} />
        <Legend hint={legendHint} />
      </div>
    )
  }

  const { vp } = viewport
  const transform = `translate(${vp.x}px, ${vp.y}px) scale(${vp.scale})`
  const muted = `${markerId}-muted`, strong = `${markerId}-strong`, warn = `${markerId}-warning`
  const toolbar = (
    <div className="flex justify-end gap-1">
      <Tip text="Zoom in"><Button variant="outline" size="sm" aria-label="Zoom in" onClick={viewport.zoomIn}><ZoomIn className="size-4" /></Button></Tip>
      <Tip text="Zoom out"><Button variant="outline" size="sm" aria-label="Zoom out" onClick={viewport.zoomOut}><ZoomOut className="size-4" /></Button></Tip>
      <Tip text="Fit to width"><Button variant="outline" size="sm" aria-label="Fit to width" onClick={viewport.fit}><Expand className="size-4" /></Button></Tip>
    </div>
  )
  return (
    <div ref={rootRef} data-testid="process-map" data-mode="map" data-columns={columns} role="group" aria-label={name} className="relative">
      {toolbarTarget === undefined ? <div className="mb-2">{toolbar}</div> : toolbarTarget ? createPortal(toolbar, toolbarTarget) : null}
      <div ref={viewportRef} data-testid="process-map-viewport" className="relative touch-none select-none overflow-hidden rounded-xl border border-border bg-background"
        style={{ height: viewport.height }} {...viewport.handlers}>
        <div data-testid="process-map-layer" className="absolute left-0 top-0 origin-top-left" style={{ width: layout.width, height: layout.height, transform }}>
          <svg aria-hidden="true" className="absolute inset-0 overflow-visible text-muted-foreground" width={layout.width} height={layout.height}>
            <defs>
              <marker id={muted} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" className="fill-muted-foreground" />
              </marker>
              <marker id={strong} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" className="fill-foreground" />
              </marker>
              <marker id={warn} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" className="fill-warning" />
              </marker>
            </defs>
            {layout.lanes.map((lane) => (
              <g key={lane.id} data-testid="process-lane">
                <rect x={lane.x} y={lane.y} width={lane.w} height={lane.h} rx={10} className="fill-muted" opacity={0.55} />
                {/* SA-454: the layout owns these anchors, so the lane label is
                    in the collision model like every other label. */}
                {lane.labels.map((box, i) => (
                  <text key={box.text + i} x={box.x} y={box.y} textAnchor={box.anchor}
                    className={`fill-muted-foreground ${i < lane.labelLines.length ? 'text-xs font-semibold' : 'text-[10.5px]'}`}>
                    {box.text}
                  </text>
                ))}
              </g>
            ))}
            {layout.edges.map((e) => {
              const on = taken.has(e.key)
              // SA-1077: a back edge is a revision path — dotted, the warning hue; a next edge stays solid.
              const tone = e.back ? (on ? 'text-warning-strong' : 'text-warning') : on ? 'text-foreground' : 'text-muted-foreground'
              return (
                <g key={e.key} data-connector={e.key} data-back-edge={String(e.back)} data-taken={on ? 'true' : 'false'}
                  className={tone} opacity={path && !on ? 0.55 : 1}>
                  <path d={e.d} fill="none" stroke="currentColor" strokeWidth={on ? 2 : 1.25} strokeDasharray={e.back ? lineDash('revision') : undefined}
                    markerEnd={`url(#${e.back ? warn : on ? strong : muted})`} />
                </g>
              )
            })}
            {layout.stubs.map((st) => {
              const on = st.lines.some((l) => taken.has(`${st.step}:${l.exit}`))
              return (
                <g key={st.step} data-stub={st.step} data-exits={st.lines.map((l) => l.exit).join(' ')} data-taken={on ? 'true' : 'false'}
                  className={on ? 'text-foreground' : 'text-muted-foreground'} opacity={path && !on ? 0.55 : 1}>
                  <path d={st.arrowD} fill="none" stroke="currentColor" strokeWidth={on ? 2 : 1.25}
                    strokeDasharray={st.lines.some((l) => l.kind === 'close') ? lineDash('outcome') : undefined} markerEnd={`url(#${on ? strong : muted})`} />
                </g>
              )
            })}
          </svg>
          {vm.steps.map((s) => (
            <MapCard key={s.id} step={s} box={cardOf.get(s.id)} current={s.id === currentStep} visited={visited.has(s.id)} skipped={skipped.has(s.id)} ring={s.id === ringStep}
              tabIndex={s.id === roving ? 0 : -1} href={stepHref?.(s.id)} onSelect={onSelect} onKeyDown={onKeyDown} onFocus={onFocus} />
          ))}
          <svg aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-visible" width={layout.width} height={layout.height}>
            {layout.edges.map((e) => (e.label ? <Label key={e.key} box={e.label} taken={taken.has(e.key)} /> : null))}
            {/* SA-454: per LINE, not per block — a taken close does not light
                the self-loop's words beside it. The arrow above stays per block. */}
            {layout.stubs.flatMap((st) => st.labels.map((box, i) => (
              <Label key={`${st.step}#${i}`} box={box} stubLabel={`${st.step}:${box.exit}`} taken={taken.has(`${st.step}:${box.exit}`)} />
            )))}
          </svg>
        </div>
      </div>
      <Legend hint={legendHint} />
    </div>
  )
}
